import { useMemo, useState } from 'react'
import cx from 'classnames'
import { useQueryClient } from 'react-query'

import { FilesTreeProvider } from 'components/FilesTree/stores/FilesTreeStore'
import { TreeDataItem } from 'components/FilesTree/types'
import { Add } from 'almanac-ui/icons'
import { useWorkspace } from 'hooks/useWorkspace'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AddFileButton } from 'components/FilesTree/components/AddFileButton/AddFileButton'
import { LocationPickerFolder, SelectedFolder } from 'components/LocationPicker/LocationPicker'
import SharedStyles from '../../FileTreeLocationPicker.module.scss'
import Styles from './Projects.module.scss'
import { useFiles } from 'hooks/queries/finder/useFiles'
import { NewFolderInlineMenu } from 'components/NewFolderInlineMenu/NewFolderInlineMenu'
import { useAuth } from 'hooks/UseAuth'
import { SubjectType } from 'utils/caslAbility'
import { Can } from 'components/Can'
import { collectResults } from 'utils/query'
import { FilesTree } from '../FilesTree/FilesTree'
import { AlmanacFile } from 'types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { IconButton } from 'components/IconButton/IconButton'
import { SectionHeader } from 'components/SectionHeader/SectionHeader'

export const Projects = ({
  workspaceId,
  onSelect,
  activeFolder,
  isRootDisabled = false,
  isCreateDisabled = false,
  disabledFolderIds = [],
  inModal = false,
}: {
  workspaceId: number
  onSelect: (folder: SelectedFolder | null) => void
  activeFolder: LocationPickerFolder | null | undefined
  isRootDisabled?: boolean
  isCreateDisabled?: boolean
  disabledFolderIds?: string[]
  inModal?: boolean
}) => {
  const [showCreateMenuInHeader, setShowCreateMenuInHeader] = useState<boolean>(false)
  const [showCreateMenuInList, setShowCreateMenuInList] = useState<boolean>(false)
  const location = 'projects'
  const currentWorkspaceId = useCurrentWorkspaceId()
  const workspace = useWorkspace(workspaceId)
  const auth = useAuth()
  const queryClient = useQueryClient()
  const query = useFiles({
    location,
    workspaceId,
    query: { items: 50 },
    disableSchema: true,
  })
  const { isMobile } = useResponsiveDesign()
  const canCreateProject = !isCreateDisabled

  const files = useMemo(
    () => collectResults(query, (data) => (data.files || []).filter((f) => !f.uploads_project)),
    [query],
  ) as TreeDataItem[]

  const handleHeaderClick = () => {
    if (isRootDisabled) return

    onSelect(null)
  }

  const onFolderCreate = (folder: AlmanacFile) => {
    if (folder.handbook) {
      onSelect({
        ...folder,
        handbook_key: folder.handbook.key,
        seo_string: folder.handbook.seo_string,
      })
    }
    if (workspaceId === currentWorkspaceId) return

    queryClient.invalidateQueries(['workspace', workspaceId, 'files'])
  }

  if (!files || files.length === 0) return null

  return (
    <li>
      <SectionHeader
        label="Projects"
        onPress={handleHeaderClick}
        isExpanded={true}
        isExpandable={false}
        inModal={inModal}
        inLocationPicker
        size="sm"
        forceShowChildren={isMobile}
      >
        {auth && !isMobile && canCreateProject && (
          <Can I="create" a={SubjectType.ALMANAC_FILE} inWorkspace={workspaceId}>
            <AddFileButton
              isRootExpanded={false}
              showInlineCreate={() => setShowCreateMenuInHeader(true)}
              createInRoot
            />
          </Can>
        )}
        {auth && isMobile && canCreateProject && (
          <Can I="create" a={SubjectType.ALMANAC_FILE} inWorkspace={workspaceId}>
            <IconButton
              data-testid="add-project-button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              onPress={() => setShowCreateMenuInHeader(true)}
              icon={Add}
              size="sm"
              aria-label="Add new file"
            />
          </Can>
        )}
      </SectionHeader>
      {auth && showCreateMenuInHeader && !!workspace && (
        <div className={SharedStyles.createInlineContainer}>
          <NewFolderInlineMenu
            doNotRedirect
            onSuccess={onFolderCreate}
            workspaceKey={workspace.key}
            onClose={() => setShowCreateMenuInHeader(false)}
            setShowCreateMenu={setShowCreateMenuInHeader}
          />
        </div>
      )}
      <FilesTreeProvider>
        <FilesTree
          onSelect={onSelect}
          activeFolder={activeFolder}
          files={files}
          hideDocs
          disabledFolderIds={disabledFolderIds}
        />
      </FilesTreeProvider>
      {auth && canCreateProject && (
        <Can I="create" a={SubjectType.ALMANAC_FILE} inWorkspace={workspaceId}>
          <div
            className={cx(SharedStyles.newProjectRow, Styles.newProjectRow, {
              [SharedStyles.hidden]: showCreateMenuInList,
            })}
            onClick={(e) => {
              e.stopPropagation()
              setShowCreateMenuInList(true)
            }}
          >
            <Add />
            New Project
          </div>
          {showCreateMenuInList && !!workspace && (
            <div className={SharedStyles.createInlineContainer}>
              <NewFolderInlineMenu
                doNotRedirect
                onSuccess={onFolderCreate}
                workspaceKey={workspace.key}
                onClose={() => setShowCreateMenuInList(false)}
                setShowCreateMenu={setShowCreateMenuInList}
              />
            </div>
          )}
        </Can>
      )}
    </li>
  )
}
